/**
 * Professional Scroll-triggered Animations
 * Uses Intersection Observer for performance
 * Supports prefers-reduced-motion
 */

(function() {
  'use strict';

  // Check for reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (prefersReducedMotion) {
    // Don't animate if user prefers reduced motion
    document.documentElement.classList.add('reduce-motion');
    return;
  }

  // Configuration
  const config = {
    rootMargin: '0px 0px -100px 0px', // Trigger 100px before element enters viewport
    threshold: [0, 0.1, 0.5, 1.0],
    animationDelay: 100, // Stagger delay in ms
  };

  // Animation classes and their effects
  const animations = {
    'fade-in': {
      initial: { opacity: 0, transform: 'translateY(20px)' },
      animate: { opacity: 1, transform: 'translateY(0)' },
    },
    'fade-in-up': {
      initial: { opacity: 0, transform: 'translateY(40px)' },
      animate: { opacity: 1, transform: 'translateY(0)' },
    },
    'fade-in-left': {
      initial: { opacity: 0, transform: 'translateX(-40px)' },
      animate: { opacity: 1, transform: 'translateX(0)' },
    },
    'fade-in-right': {
      initial: { opacity: 0, transform: 'translateX(40px)' },
      animate: { opacity: 1, transform: 'translateX(0)' },
    },
    'scale-in': {
      initial: { opacity: 0, transform: 'scale(0.9)' },
      animate: { opacity: 1, transform: 'scale(1)' },
    },
    'stagger-children': {
      // Special case: animate children with delay
      stagger: true,
    },
  };

  // Initialize animations on elements
  function initScrollAnimations() {
    const elements = document.querySelectorAll('[data-scroll-animation]');
    
    if (!elements.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          const element = entry.target;
          const animationType = element.dataset.scrollAnimation;
          const delay = parseInt(element.dataset.scrollDelay) || 0;
          
          if (animations[animationType]?.stagger) {
            animateChildren(element);
          } else {
            setTimeout(() => {
              element.classList.add('is-visible');
            }, delay);
          }
          
          // Unobserve after animation (one-time animation)
          if (element.dataset.scrollOnce !== 'false') {
            observer.unobserve(element);
          }
        } else {
          // If repeat animation is enabled
          if (entry.target.dataset.scrollOnce === 'false') {
            entry.target.classList.remove('is-visible');
          }
        }
      });
    }, config);

    elements.forEach((element) => {
      // Set initial state
      const animationType = element.dataset.scrollAnimation;
      const animation = animations[animationType];
      
      if (animation && !animation.stagger) {
        Object.assign(element.style, animation.initial);
        element.style.transition = 'opacity 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)';
      }
      
      observer.observe(element);
    });
  }

  // Animate children with stagger effect
  function animateChildren(parentElement) {
    const children = parentElement.querySelectorAll('[data-stagger-item]');
    const staggerDelay = parseInt(parentElement.dataset.staggerDelay) || config.animationDelay;
    
    children.forEach((child, index) => {
      // Set initial state
      Object.assign(child.style, {
        opacity: 0,
        transform: 'translateY(20px)',
        transition: 'opacity 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
      });
      
      // Animate with delay
      setTimeout(() => {
        Object.assign(child.style, {
          opacity: 1,
          transform: 'translateY(0)',
        });
      }, index * staggerDelay);
    });
    
    parentElement.classList.add('is-visible');
  }

  // Parallax effect on scroll (subtle)
  function initParallaxEffect() {
    const parallaxElements = document.querySelectorAll('[data-parallax]');
    
    if (!parallaxElements.length) return;

    let ticking = false;

    function updateParallax() {
      parallaxElements.forEach((element) => {
        const speed = parseFloat(element.dataset.parallaxSpeed) || 0.5;
        const rect = element.getBoundingClientRect();
        const scrollPercent = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        
        if (scrollPercent >= 0 && scrollPercent <= 1) {
          const yOffset = (scrollPercent - 0.5) * 50 * speed;
          element.style.transform = `translateY(${yOffset}px)`;
        }
      });
      
      ticking = false;
    }

    function requestTick() {
      if (!ticking) {
        requestAnimationFrame(updateParallax);
        ticking = true;
      }
    }

    window.addEventListener('scroll', requestTick, { passive: true });
    updateParallax(); // Initial call
  }

  // Progress bar on scroll
  function initScrollProgress() {
    const progressBar = document.querySelector('[data-scroll-progress]');
    
    if (!progressBar) return;

    let ticking = false;

    function updateProgress() {
      const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100;
      progressBar.style.width = `${scrollPercent}%`;
      ticking = false;
    }

    function requestTick() {
      if (!ticking) {
        requestAnimationFrame(updateProgress);
        ticking = true;
      }
    }

    window.addEventListener('scroll', requestTick, { passive: true });
    updateProgress(); // Initial call
  }

  // Add CSS for visible state
  function addAnimationStyles() {
    const style = document.createElement('style');
    style.textContent = `
      [data-scroll-animation].is-visible {
        opacity: 1 !important;
        transform: translate(0, 0) scale(1) !important;
      }
      
      /* Reduced motion override */
      .reduce-motion [data-scroll-animation],
      .reduce-motion [data-parallax] {
        opacity: 1 !important;
        transform: none !important;
        transition: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  // Initialize everything when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    addAnimationStyles();
    initScrollAnimations();
    initParallaxEffect();
    initScrollProgress();
  }

  // Expose API for dynamic content
  window.ScrollAnimations = {
    refresh: initScrollAnimations,
  };
})();
